import { useEffect, useMemo, useState, useRef } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2,
  Plus,
  Trash2,
  Image as ImageIcon,
  X,
  Upload,
  Save,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useMenu } from "../context/MenuContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rivaaz_admin_token";
const ALL_TAGS = ["veg", "gf", "nuts", "spicy", "signature"];

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function AdminMenu() {
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);
  const { categories, loading, reload } = useMenu();
  const [editing, setEditing] = useState(null); // {mode: 'item'|'category'|'new-item', data}

  const headers = { Authorization: `Bearer ${token}` };

  const totalItems = useMemo(
    () => categories.reduce((s, c) => s + c.items.length, 0),
    [categories]
  );

  if (!token) return <Navigate to="/admin/login" replace />;

  const onDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/menu/items/${item.id}`, { headers });
      toast.success(`Deleted ${item.name}`);
      reload();
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login");
        return;
      }
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="admin-menu-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <Link
            to="/admin"
            data-testid="admin-menu-back"
            className="inline-flex items-center gap-1 text-xs font-bold text-[#57534E] hover:text-[#C9853E]"
          >
            <ArrowLeft className="w-3 h-3" /> Back to orders
          </Link>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[#0E0E1A] mt-2">
            Menu editor
          </h1>
          <p className="text-[#57534E] text-sm mt-1">
            {categories.length} categories · {totalItems} items · changes are live immediately.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#57534E]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading menu…
        </div>
      ) : (
        <div className="space-y-12" data-testid="admin-menu-categories">
          {categories.map((cat) => (
            <section key={cat.slug} data-testid={`admin-cat-${cat.slug}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl overflow-hidden bg-[#0E0E1A] shrink-0">
                    {cat.image_url && (
                      <img
                        src={cat.image_url}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-extrabold text-[#0E0E1A]">
                      {cat.name}
                    </h2>
                    <p className="text-xs text-[#57534E] mt-0.5">
                      {cat.items.length} items
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`admin-edit-cat-${cat.slug}`}
                    onClick={() => setEditing({ mode: "category", data: cat })}
                    className="inline-flex items-center gap-1.5 bg-white border border-[#E7E5E4] hover:border-[#0E0E1A] text-[#0E0E1A] rounded-full px-4 py-2 text-xs font-bold"
                  >
                    <ImageIcon className="w-3 h-3" /> Edit category
                  </button>
                  <button
                    data-testid={`admin-add-item-${cat.slug}`}
                    onClick={() =>
                      setEditing({
                        mode: "new-item",
                        data: {
                          category_slug: cat.slug,
                          name: "",
                          desc: "",
                          price: "",
                          note: "",
                          tags: [],
                          variants: [],
                        },
                      })
                    }
                    className="inline-flex items-center gap-1.5 bg-[#C9853E] hover:bg-[#A6692D] text-white rounded-full px-4 py-2 text-xs font-bold"
                  >
                    <Plus className="w-3 h-3" /> Add item
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {cat.items.map((item) => (
                  <article
                    key={item.id}
                    data-testid={`admin-item-${item.id}`}
                    className="bg-white border border-[#E7E5E4] rounded-2xl p-4 flex gap-3"
                  >
                    <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 bg-[#FAF8F5] flex items-center justify-center">
                      {item.image_data ? (
                        <img
                          src={item.image_data}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-[#A8A29E]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <h3 className="font-display font-bold text-[#0E0E1A] truncate">
                          {item.name}
                        </h3>
                        <span className="text-xs font-bold text-[#C9853E] whitespace-nowrap">
                          {item.price != null
                            ? `$${item.price.toFixed(2)}`
                            : item.note || "—"}
                        </span>
                      </div>
                      {item.variants?.length > 0 && (
                        <div className="text-[10px] text-[#57534E] mt-0.5">
                          {item.variants.length} variants
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {item.tags?.map((t) => (
                          <span
                            key={t}
                            className="text-[9px] tracking-[0.18em] uppercase font-bold bg-[#FAF8F5] border border-[#E7E5E4] rounded-full px-1.5 py-0.5 text-[#57534E]"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          data-testid={`admin-edit-${item.id}`}
                          onClick={() => setEditing({ mode: "item", data: item })}
                          className="flex-1 bg-[#0E0E1A] hover:bg-[#1C1B2B] text-white rounded-full px-3 py-1.5 text-[10px] font-bold"
                        >
                          Edit
                        </button>
                        <button
                          data-testid={`admin-delete-${item.id}`}
                          onClick={() => onDelete(item)}
                          className="bg-white border border-[#E7E5E4] hover:border-[#C9853E] hover:text-[#C9853E] text-[#57534E] rounded-full px-3 py-1.5"
                          aria-label="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <EditorDialog
          mode={editing.mode}
          data={editing.data}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
          headers={headers}
          onAuthFail={() => {
            localStorage.removeItem(TOKEN_KEY);
            navigate("/admin/login");
          }}
        />
      )}
    </div>
  );
}

function EditorDialog({ mode, data, onClose, onSaved, headers, onAuthFail }) {
  const [form, setForm] = useState(() => ({
    name: data.name || "",
    desc: data.desc || "",
    price: data.price ?? "",
    note: data.note || "",
    tags: data.tags || [],
    variants: data.variants || [],
    image_data: data.image_data ?? data.image_url ?? "",
    intro: data.intro || "",
  }));
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const isCategory = mode === "category";
  const isNew = mode === "new-item";

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      toast.error("Image too large — max 1.5 MB. Please compress first.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      update("image_data", dataUrl);
    } catch {
      toast.error("Couldn't read image");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isCategory) {
        await axios.patch(
          `${API}/admin/menu/categories/${data.slug}`,
          {
            intro: form.intro,
            note: form.note,
            image_data: form.image_data || "",
          },
          { headers }
        );
        toast.success(`Updated ${data.name}`);
      } else {
        const payload = {
          name: form.name,
          desc: form.desc,
          price: form.variants.length
            ? null
            : form.price === ""
            ? null
            : Number(form.price),
          note: form.note || null,
          tags: form.tags,
          variants: form.variants.map((v) => ({
            name: v.name,
            price: Number(v.price),
          })),
          image_data: form.image_data || "",
        };
        if (isNew) {
          payload.category_slug = data.category_slug;
          await axios.post(`${API}/admin/menu/items`, payload, { headers });
          toast.success("Item added");
        } else {
          await axios.patch(`${API}/admin/menu/items/${data.id}`, payload, {
            headers,
          });
          toast.success("Item updated");
        }
      }
      onSaved();
    } catch (e) {
      if (e?.response?.status === 401) {
        onAuthFail();
        return;
      }
      const msg = e?.response?.data?.detail || e?.message || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent
        className="sm:max-w-lg bg-white border-[#E7E5E4] max-h-[90vh] overflow-y-auto"
        data-testid="admin-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-[#0E0E1A]">
            {isCategory
              ? `Edit ${data.name}`
              : isNew
              ? "Add menu item"
              : `Edit ${data.name}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Image upload */}
          <div>
            <Label>Photo</Label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-[#FAF8F5] border border-[#E7E5E4] flex items-center justify-center shrink-0">
                {form.image_data ? (
                  <img src={form.image_data} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-[#A8A29E]" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  data-testid="admin-image-input"
                  onChange={onPickFile}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="admin-image-upload-btn"
                  className="inline-flex items-center gap-1.5 bg-[#0E0E1A] hover:bg-[#1C1B2B] text-white rounded-full px-4 py-2 text-xs font-bold"
                >
                  <Upload className="w-3 h-3" /> Upload photo
                </button>
                {form.image_data && (
                  <button
                    type="button"
                    onClick={() => update("image_data", "")}
                    data-testid="admin-image-clear-btn"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#57534E] hover:text-[#C9853E]"
                  >
                    <X className="w-3 h-3" /> Remove photo
                  </button>
                )}
              </div>
            </div>
            <div className="text-[10px] text-[#A8A29E] mt-2">
              JPG/PNG · max 1.5 MB. Tip: compress photos before uploading.
            </div>
          </div>

          {!isCategory && (
            <>
              <Field
                label="Name"
                required
                testid="admin-field-name"
                value={form.name}
                onChange={(v) => update("name", v)}
              />
              <Field
                label="Description"
                multiline
                testid="admin-field-desc"
                value={form.desc}
                onChange={(v) => update("desc", v)}
              />

              {form.variants.length === 0 ? (
                <Field
                  label="Price (CAD)"
                  type="number"
                  step="0.01"
                  testid="admin-field-price"
                  value={form.price}
                  onChange={(v) => update("price", v)}
                />
              ) : (
                <div className="bg-[#FAF8F5] border border-[#E7E5E4] rounded-2xl p-3 text-xs text-[#57534E]">
                  This item uses variants. Single price disabled.
                </div>
              )}

              <Field
                label="Tag (e.g. 'Choose Size' or 'Choose Protein')"
                testid="admin-field-note"
                value={form.note}
                onChange={(v) => update("note", v)}
              />

              <div>
                <Label>Dietary tags</Label>
                <div className="flex flex-wrap gap-2" data-testid="admin-tags">
                  {ALL_TAGS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`admin-tag-${t}`}
                      onClick={() =>
                        update(
                          "tags",
                          form.tags.includes(t)
                            ? form.tags.filter((x) => x !== t)
                            : [...form.tags, t]
                        )
                      }
                      className={`text-[10px] tracking-[0.18em] uppercase font-bold rounded-full px-3 py-1.5 border ${
                        form.tags.includes(t)
                          ? "bg-[#C9853E] border-[#C9853E] text-white"
                          : "bg-white border-[#E7E5E4] text-[#0E0E1A]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <VariantsEditor
                variants={form.variants}
                onChange={(v) => update("variants", v)}
              />
            </>
          )}

          {isCategory && (
            <>
              <Field
                label="Intro line"
                multiline
                testid="admin-field-intro"
                value={form.intro}
                onChange={(v) => update("intro", v)}
              />
              <Field
                label="Note (optional)"
                multiline
                testid="admin-field-cat-note"
                value={form.note}
                onChange={(v) => update("note", v)}
              />
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-6 sticky bottom-0 bg-white pt-4">
          <button
            onClick={onClose}
            className="bg-white border border-[#E7E5E4] text-[#0E0E1A] rounded-full px-5 py-2.5 text-sm font-bold"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-editor-save-btn"
            className="inline-flex items-center gap-2 bg-[#C9853E] hover:bg-[#A6692D] disabled:opacity-60 text-white rounded-full px-6 py-2.5 text-sm font-bold"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Label = ({ children }) => (
  <span className="text-xs font-semibold tracking-wide uppercase text-[#57534E] mb-1.5 block">
    {children}
  </span>
);

const Field = ({ label, value, onChange, multiline, type = "text", step, testid, required }) => (
  <label className="block">
    <Label>
      {label} {required && <span className="text-[#C9853E]">*</span>}
    </Label>
    {multiline ? (
      <textarea
        data-testid={testid}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-2.5 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-2.5 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
      />
    )}
  </label>
);

function VariantsEditor({ variants, onChange }) {
  const addVariant = () =>
    onChange([...variants, { name: "", price: 0 }]);
  const updateVariant = (i, k, v) =>
    onChange(variants.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const removeVariant = (i) =>
    onChange(variants.filter((_, idx) => idx !== i));

  return (
    <div data-testid="admin-variants-editor">
      <div className="flex items-center justify-between mb-2">
        <Label>Variants (sizes / proteins / flavours)</Label>
        <button
          type="button"
          data-testid="admin-add-variant-btn"
          onClick={addVariant}
          className="text-xs font-bold text-[#C9853E] hover:underline"
        >
          + Add variant
        </button>
      </div>
      {variants.length === 0 ? (
        <p className="text-xs text-[#A8A29E]">
          No variants. Add one (e.g. 10" Small / 15" Large) to support different sizes or proteins.
        </p>
      ) : (
        <div className="space-y-2">
          {variants.map((v, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`admin-variant-${i}`}>
              <input
                placeholder={`Variant name (e.g. 10" Small)`}
                value={v.name}
                onChange={(e) => updateVariant(i, "name", e.target.value)}
                className="flex-1 bg-white border border-[#E7E5E4] rounded-xl px-3 py-2 text-sm text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
              />
              <input
                type="number"
                step="0.01"
                placeholder="Price"
                value={v.price}
                onChange={(e) => updateVariant(i, "price", e.target.value)}
                className="w-24 bg-white border border-[#E7E5E4] rounded-xl px-3 py-2 text-sm text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
              />
              <button
                type="button"
                onClick={() => removeVariant(i)}
                className="text-[#57534E] hover:text-[#C9853E] p-1.5"
                aria-label="Remove variant"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
